import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { Image as ImageIcon } from "lucide-react";
import type { RouterOutputs } from "../trpc";

type Template = RouterOutputs["whatsappTemplates"]["list"]["templates"][number];

type TemplatePreviewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  template: Template | null;
};

const TemplatePreviewModal = ({
  isOpen,
  onClose,
  template,
}: TemplatePreviewModalProps): JSX.Element => {
  const [imageError, setImageError] = useState(false);

  const getLanguageLabel = (code: string): string => {
    const languages: Record<string, string> = {
      es: "Español",
      en: "Inglés",
      pt: "Portugués",
    };
    return languages[code] || code;
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case "MARKETING":
        return "Marketing";
      case "UTILITY":
        return "Utilidad";
      case "AUTHENTICATION":
        return "Autenticación";
      default:
        return category;
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      PENDING: { label: "Pendiente", className: "bg-yellow-100 text-yellow-800" },
      APPROVED: { label: "Aprobada", className: "bg-green-100 text-green-800" },
      REJECTED: { label: "Rechazada", className: "" },
      DELETED: { label: "Eliminada", className: "bg-red-200 text-red-900" },
      FAILED: { label: "Fallida", className: "" },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || {
      label: status,
      className: "bg-gray-100 text-gray-800",
    };

    if (status === "REJECTED" || status === "FAILED") {
      return <Badge variant="destructive">{config.label}</Badge>;
    }

    return <Badge className={config.className}>{config.label}</Badge>;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="h-full max-h-full md:h-auto md:max-h-[85vh] md:max-w-lg">
        <DialogHeader>
          <DialogTitle>Vista previa de plantilla</DialogTitle>
        </DialogHeader>
        <div className="pb-6">
          {!template || !template.config ? (
            <p>No se pudo cargar la plantilla</p>
          ) : (
            <div className="flex flex-col gap-4">
              <div>
                <p className="mb-1 text-sm font-bold text-gray-600">
                  Nombre
                </p>
                <p className="text-base">{template.name}</p>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <p className="mb-1 text-sm font-bold text-gray-600">
                    Categoría
                  </p>
                  <p className="text-base">{getCategoryLabel(template.category)}</p>
                </div>

                <div className="flex-1">
                  <p className="mb-1 text-sm font-bold text-gray-600">
                    Idioma
                  </p>
                  <p className="text-base">{getLanguageLabel(template.language)}</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <p className="mb-1 text-sm font-bold text-gray-600">
                    Estado
                  </p>
                  {getStatusBadge(template.status)}
                </div>

                <div className="flex-1">
                  <p className="mb-1 text-sm font-bold text-gray-600">
                    WABA ID
                  </p>
                  <p className="font-mono text-sm text-gray-700">
                    {template.wabaId}
                  </p>
                </div>
              </div>

              {(template.status === "FAILED" || template.status === "REJECTED") &&
                template.statusReason && (
                  <Alert status="error">
                    <p className="mb-1 text-sm font-bold">
                      {template.status === "FAILED" ? "Razón del fallo" : "Razón del rechazo"}
                    </p>
                    <p className="whitespace-pre-wrap text-sm">{template.statusReason}</p>
                    {template.statusReasonCode && (
                      <p className="mt-1 text-xs opacity-70">
                        Código Meta: {template.statusReasonCode}
                      </p>
                    )}
                  </Alert>
                )}

              {template.config.category !== "AUTHENTICATION" && template.config.header && (
                <div>
                  <p className="mb-2 text-sm font-bold text-gray-600">
                    Encabezado
                  </p>
                  <div className="overflow-hidden rounded-md border bg-gray-50 text-center">
                    {template.config.header.link && !imageError ? (
                      template.config.header.format === "video" ? (
                        <video
                          src={template.config.header.link}
                          className="max-h-[200px] w-full object-cover"
                          controls
                          onError={() => setImageError(true)}
                        />
                      ) : (
                        <img
                          src={template.config.header.link}
                          alt="Header"
                          className="max-h-[200px] w-full object-cover"
                          onError={() => setImageError(true)}
                        />
                      )
                    ) : (
                      <div className="p-4 text-gray-500">
                        <ImageIcon className="mx-auto mb-2 text-2xl" />
                        <p className="text-xs">
                          {imageError
                            ? "No se pudo cargar el archivo"
                            : template.config.header.format === "video"
                              ? "Video configurado"
                              : "Imagen configurada"}
                        </p>
                        {template.config.header.link && (
                          <p className="mt-1 truncate text-xs text-gray-400">
                            {template.config.header.link}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div>
                <p className="mb-2 text-sm font-bold text-gray-600">
                  Contenido
                </p>
                <div className="whitespace-pre-wrap rounded-md border bg-gray-50 p-4">
                  <p className="text-sm">{template.bodyPreview}</p>
                </div>
              </div>

              {template.config.category === "AUTHENTICATION" && (
                <>
                  {template.config.codeExpirationMinutes !== undefined && (
                    <div>
                      <p className="mb-2 text-sm font-bold text-gray-600">
                        Pie de página
                      </p>
                      <div className="rounded-md border bg-gray-50 p-4">
                        <p className="text-sm text-gray-600">
                          Este código caduca en {template.config.codeExpirationMinutes} minutos.
                        </p>
                      </div>
                    </div>
                  )}

                  <div>
                    <p className="mb-2 text-sm font-bold text-gray-600">
                      Botón de copiado
                    </p>
                    <div className="rounded-md border bg-gray-50 p-3 text-center text-sm font-medium text-blue-500">
                      {template.config.otpButtonText || "Copiar código"}
                    </div>
                  </div>
                </>
              )}

              {template.config.category !== "AUTHENTICATION" &&
                template.config.buttons &&
                template.config.buttons.length > 0 && (
                  <div>
                    <p className="mb-2 text-sm font-bold text-gray-600">
                      Botones de respuesta rápida
                    </p>
                    <div className="flex flex-col gap-2">
                      {template.config.buttons.map((button, index) => (
                        <div
                          key={index}
                          className="rounded-md border bg-gray-50 p-3 text-center text-sm font-medium text-blue-500"
                        >
                          {button.text}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {template.config.category !== "AUTHENTICATION" &&
                template.config.body.defaults &&
                Object.keys(template.config.body.defaults).length > 0 && (
                  <div>
                    <p className="mb-2 text-sm font-bold text-gray-600">
                      Valores por defecto
                    </p>
                    <div className="flex flex-col gap-2">
                      {Object.entries(template.config.body.defaults).map(([key, value]) => (
                        <div key={key} className="flex items-center gap-2">
                          <span className="text-sm font-medium">
                            {`{{${key}}}:`}
                          </span>
                          <span className="text-sm text-gray-600">
                            {value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TemplatePreviewModal;
